import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearch } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Stepper } from "@/components/ui/stepper";

import { ActionsStep } from "./components/ActionsStep";
import { IssuesStep } from "./components/IssuesStep";
import { PreviewStep } from "./components/PreviewStep";
import { ProfileStep } from "./components/ProfileStep";
import type { AuthoringStep } from "./types";

const STEPS: AuthoringStep[] = ["profile", "issues", "actions", "preview"];

/**
 * The 4-step communication authoring wizard: profile → issues → actions →
 * preview/submit (matches the legacy authoring order — Profil → Sélection des
 * enjeux → Personnalisation → Prévisualisation; the profile step holds only the
 * narrative — identity/contact come from the identification profile).
 * Steps persist independently (each autosaves or mutates on change), so the
 * wizard is pure client-side navigation — no draft to carry between steps.
 */
export function AuthoringFlow() {
  const { t } = useTranslation();
  // `?step=` reopens the wizard where a Back from elsewhere left it.
  const search = useSearch({ strict: false }) as { step?: AuthoringStep };
  const [step, setStep] = useState<AuthoringStep>(search.step ?? "profile");
  const index = STEPS.indexOf(step);

  return (
    <div className="space-y-6">
      <Stepper
        steps={STEPS.map((s) => ({
          key: s,
          label: t(`communications.steps.${s}`),
        }))}
        current={step}
        onSelect={(key) => setStep(key as AuthoringStep)}
        ariaLabel={t("communications.stepsLabel")}
      />

      {step === "profile" && <ProfileStep />}
      {step === "issues" && <IssuesStep />}
      {step === "actions" && <ActionsStep />}
      {step === "preview" && <PreviewStep />}

      <div className="flex justify-between">
        <Button
          variant="outline"
          disabled={index === 0}
          onClick={() => {
            const prev = STEPS[index - 1];
            if (prev) setStep(prev);
          }}
        >
          {t("communications.back")}
        </Button>
        <Button
          disabled={index === STEPS.length - 1}
          onClick={() => {
            const next = STEPS[index + 1];
            if (next) setStep(next);
          }}
        >
          {t("communications.next")}
        </Button>
      </div>
    </div>
  );
}
